import { useSubscription } from '@/hooks/useSubscription';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTextActions } from './useTextActions';
import { useTextDoc } from './useTextDoc';
import { useTextList } from './useTextList';

interface EditorControllerProps {
  selectedTextId?: string;
}

/**
 * Master Controller Hook for the /editor page.
 * Orchestrates text listing, document detailed state, and all mutations.
 * Simplifies editor.tsx by providing a single point of interaction.
 */
export function useEditorController({ selectedTextId }: EditorControllerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();
  const canUseAutosave = hasFeature('autosave');

  // ── States ──────────────────────────────
  const [autosaveEnabled, setAutosaveEnabled] = useState(true);

  // ── Hooks ───────────────────────────────
  const { textSummaries, isLoadingTexts } = useTextList();
  const { createNewText, renameText, deleteTextDoc } = useTextActions();

  const {
    currentTextDoc,
    editorContent,
    setEditorContent,
    isLoadingText,
    isAutosaving,
    isDirty,
    hasConflict,
    reloadRemoteVersion,
    persistCommentChange,
    saveManually,
  } = useTextDoc({
    contentId: selectedTextId,
    autosaveEnabled: autosaveEnabled && canUseAutosave,
    debounceMs: 60000,
  });

  // ── Handlers ─────────────────────────────
  const selectText = (id: string | undefined) => {
    navigate({
      to: '/editor',
      search: { textId: id },
    });
  };

  const handleCreateNew = async (name: string) => {
    const newId = await createNewText(name);
    if (newId) {
      selectText(newId);
    }
    return newId;
  };

  const handleRename = async (textId: string, newName: string) => {
    await renameText(textId, newName);
  };

  const handleDelete = async (textId: string) => {
    await deleteTextDoc(textId);
    if (textId === selectedTextId) {
      selectText(undefined);
    }
  };

  const preserveConflictAsVariant = async (sceneId: string | undefined, sceneName: string) => {
    if (!hasConflict || !sceneId) return null;
    const timestamp = new Date().toLocaleString();
    const recoveredName = t('editor.recovered_variant_name', { sceneName, timestamp });
    return createNewText(recoveredName, [sceneId], editorContent);
  };

  const toggleAutosave = () => {
    if (canUseAutosave) {
      setAutosaveEnabled(!autosaveEnabled);
    }
  };

  return {
    // Selection
    selectedTextId,
    selectText,

    // Data
    textSummaries,
    currentTextDoc,
    editorContent,
    setEditorContent,

    // Status
    isLoadingTexts,
    isLoadingText,
    isAutosaving,
    isDirty,
    hasConflict,
    autosaveEnabled,
    canUseAutosave,

    // Actions
    handleSave: saveManually,
    handleCommentChange: persistCommentChange,
    reloadRemoteVersion,
    preserveConflictAsVariant,
    handleCreateNew,
    handleRename,
    handleDelete,
    toggleAutosave,
  };
}
